import React, { useState, useRef } from 'react';
import { API } from '../../services/api';
import './OperatorImportModal.css';

function OperatorImportModal({ mailbox, onImportComplete, onClose }) {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            // Vérifier l'extension
            const ext = selectedFile.name.split('.').pop().toLowerCase();
            if (!['csv', 'xlsx', 'xls'].includes(ext)) {
                alert('Format de fichier non supporté. Utilisez CSV.');
                return;
            }
            setFile(selectedFile);
            setResult(null);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            const ext = droppedFile.name.split('.').pop().toLowerCase();
            if (!['csv', 'xlsx', 'xls'].includes(ext)) {
                alert('Format de fichier non supporté. Utilisez CSV.');
                return;
            }
            setFile(droppedFile);
            setResult(null);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleImport = async () => {
        if (!file) return;

        setLoading(true);
        setResult(null);

        try {
            const response = await API.importOperators(mailbox.id, file);
            if (response && response.data) {
                setResult({
                    success: true,
                    imported: response.data.imported,
                    errors: response.data.errors || []
                });

                // Si import réussi sans erreurs, notifier le parent
                if (response.data.errors?.length === 0) {
                    setTimeout(() => {
                        onImportComplete();
                    }, 2000);
                }
            }
        } catch (error) {
            console.error('Erreur import:', error);
            setResult({
                success: false,
                message: error.message
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadTemplate = () => {
        const templateUrl = API.getOperatorTemplateUrl();
        const token = localStorage.getItem('access_token');

        // Créer un lien temporaire pour télécharger avec authentification
        fetch(templateUrl, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        })
            .then(response => response.blob())
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'operators_template.csv';
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            })
            .catch(err => {
                console.error('Erreur téléchargement template:', err);
                alert('Erreur lors du téléchargement du template');
            });
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="import-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Importer des opérateurs</h3>
                    <span className="mailbox-context">pour {mailbox.email}</span>
                    <button className="modal-close" onClick={onClose}>&times;</button>
                </div>

                <div className="modal-body">
                    {/* Instructions */}
                    <div className="import-instructions">
                        <p>
                            Importez des opérateurs depuis un fichier CSV.
                            Le fichier doit contenir les colonnes suivantes :
                        </p>
                        <ul>
                            <li><strong>matricule</strong> (requis) - Code unique de l'opérateur</li>
                            <li><strong>first_name</strong> (requis) - Prénom</li>
                            <li><strong>last_name</strong> (requis) - Nom</li>
                            <li>title, department, phone, mobile, etc. (optionnels)</li>
                        </ul>
                        <button className="btn-link" onClick={handleDownloadTemplate}>
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Télécharger le template CSV
                        </button>
                    </div>

                    {/* Zone de drop */}
                    <div
                        className={`drop-zone ${file ? 'has-file' : ''}`}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />

                        {file ? (
                            <div className="file-info">
                                <svg width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span className="file-name">{file.name}</span>
                                <span className="file-size">
                                    ({(file.size / 1024).toFixed(1)} Ko)
                                </span>
                                <button
                                    className="remove-file"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setFile(null);
                                        setResult(null);
                                    }}
                                >
                                    &times;
                                </button>
                            </div>
                        ) : (
                            <>
                                <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                <p>Glissez-déposez un fichier CSV ici</p>
                                <span>ou cliquez pour parcourir</span>
                            </>
                        )}
                    </div>

                    {/* Résultat de l'import */}
                    {result && (
                        <div className={`import-result ${result.success ? 'success' : 'error'}`}>
                            {result.success ? (
                                <>
                                    <div className="result-header">
                                        <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span>{result.imported} opérateur(s) importé(s)</span>
                                    </div>
                                    {result.errors?.length > 0 && (
                                        <div className="import-errors">
                                            <p>{result.errors.length} erreur(s) :</p>
                                            <ul>
                                                {result.errors.slice(0, 5).map((err, i) => (
                                                    <li key={i}>
                                                        Ligne {err.row}: {err.error}
                                                    </li>
                                                ))}
                                                {result.errors.length > 5 && (
                                                    <li>... et {result.errors.length - 5} autres erreurs</li>
                                                )}
                                            </ul>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="result-header">
                                    <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                            d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>{result.message}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button className="btn-secondary" onClick={onClose}>
                        {result?.success && result?.errors?.length === 0 ? 'Fermer' : 'Annuler'}
                    </button>
                    {(!result || result.errors?.length > 0) && (
                        <button
                            className="btn-primary"
                            onClick={handleImport}
                            disabled={!file || loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-small"></span>
                                    Import en cours...
                                </>
                            ) : (
                                'Importer'
                            )}
                        </button>
                    )}
                    {result?.success && result?.errors?.length > 0 && (
                        <button className="btn-primary" onClick={onImportComplete}>
                            Terminer
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default OperatorImportModal;
